import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "dateSelect",
    "deviceContainer",
    "deviceSelectorButton",
    "deviceSelect",
    "displayDevice",
    "deviceText"
  ]

  static classes = ["hidden"]

  static values = {
    dateFormat: { type: String, default: "DD-MM-YYYY" },
    persisted: { type: Boolean, default: false }
  }

  connect() {
    this.initializeDateRangePicker()
  }

  disconnect() {
    this.cleanupDateRangePicker()
  }

  dispatchFlashError(message, context = null) {
    const event = new CustomEvent('flash:error', {
      detail: { message, context },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  initializeDateRangePicker() {
    if (!this.hasDateSelectTarget) return

    if (!window.$ || !$.fn.daterangepicker) {
      this.dispatchFlashError('DateRangePicker library not available', 'Date Selection')
      return
    }

    try {
      const config = this.getDateRangePickerConfig()
      $(this.dateSelectTarget).daterangepicker(config)
      this.bindDateRangePickerEvents()
    } catch (error) {
      this.dispatchFlashError(`Failed to initialize date picker: ${error.message}`, 'Date Selection')
    }
  }

  getDateRangePickerConfig() {
    const startDate = this.dateSelectTarget.dataset.startValue
    const endDate = this.dateSelectTarget.dataset.endValue

    return {
      startDate: startDate || Date.now,
      endDate: endDate || Date.now,
      opens: 'right',
      applyButtonClasses: "bg-purple-dark text-white rounded-md",
      cancelButtonClasses: "text-purple-dark",
      locale: {
        format: this.dateFormatValue,
        cancelLabel: 'Cancel'
      }
    }
  }

  bindDateRangePickerEvents() {
    $(this.dateSelectTarget)
      .on('apply.daterangepicker', this.handleDateRangeApply.bind(this))
      .on('cancel.daterangepicker', this.handleDateRangeCancel.bind(this))
  }

  handleDateRangeApply(ev, picker) {
    try {
      this.updateDateInputs(picker)
      this.updateDateDisplay(picker)
      this.toggleDateElements(true)
    } catch (error) {
      this.dispatchFlashError(`Error applying date range: ${error.message}`, 'Date Selection')
    }
  }

  handleDateRangeCancel(ev, picker) {
    const persistedValue = ev.currentTarget.dataset.persisted
    if (!persistedValue) {
      this.toggleDateElements(false)
    }
  }

  updateDateInputs(picker) {
    const startDateInput = picker.element.find('[id$="_start_date"]')
    const endDateInput = picker.element.find('[id$="_end_date"]')

    if (startDateInput.length) {
      startDateInput.val(picker.startDate.format(this.dateFormatValue))
    }
    if (endDateInput.length) {
      endDateInput.val(picker.endDate.format(this.dateFormatValue))
    }
  }

  updateDateDisplay(picker) {
    const dateContent = picker.element.find('#dateContent .date-content')
    const dateText = `From ${picker.startDate.format(this.dateFormatValue)} to ${picker.endDate.format(this.dateFormatValue)}`

    if (dateContent.length) {
      dateContent.text(dateText)
    }
  }

  toggleDateElements(showContent) {
    const dateContent = $(this.dateSelectTarget).find('#dateContent')
    const datePicker = $(this.dateSelectTarget).find('#datePicker')

    if (showContent) {
      this.removeClass(dateContent, 'hidden')
      this.addClass(datePicker, 'hidden')
    } else {
      this.addClass(dateContent, 'hidden')
      this.removeClass(datePicker, 'hidden')
    }
  }

  cleanupDateRangePicker() {
    if (this.hasDateSelectTarget && window.$ && $.fn.daterangepicker) {
      try {
        $(this.dateSelectTarget).off('.daterangepicker')
      } catch (error) {
        this.dispatchFlashError(`Error cleaning up date picker: ${error.message}`, 'Date Selection')
      }
    }
  }

  toggleDeviceSelection() {
    try {
      this.showElement(this.deviceContainerTarget)
    } catch (error) {
      this.dispatchFlashError(`Error showing device selection: ${error.message}`, 'Device Selection')
    }
  }

  saveDevice() {
    if (!this.hasDeviceSelectTarget) {
      this.dispatchFlashError('Device selection not available', 'Device Selection')
      return
    }

    if (!this.deviceSelectTarget.value.trim()) {
      this.dispatchFlashError('Please select a device', 'Device Selection')
      return
    }

    try {
      const selectedDevice = this.deviceSelectTarget.value

      this.hideElement(this.deviceContainerTarget)
      this.hideElement(this.deviceSelectorButtonTarget)
      this.showElement(this.displayDeviceTarget)
      this.showElement(this.deviceTextTarget)

      this.deviceTextTarget.textContent = selectedDevice
    } catch (error) {
      this.dispatchFlashError(`Error saving device: ${error.message}`, 'Device Selection')
      this.cancelDevice()
    }
  }

  cancelDevice(event) {
    const persistedValue = event.target.dataset.persisted
    try {
      this.hideElement(this.deviceContainerTarget)
      if(!persistedValue) {
        this.showElement(this.deviceSelectorButtonTarget)
        this.hideElement(this.deviceTextTarget)
        this.hideElement(this.displayDeviceTarget)
      }
    } catch (error) {
      this.dispatchFlashError(`Error cancelling device selection: ${error.message}`, 'Device Selection')
    }
  }

  editDevice() {
    try {
      this.showElement(this.deviceContainerTarget)
      this.hideElement(this.deviceSelectorButtonTarget)
      this.hideElement(this.displayDeviceTarget)
      this.hideElement(this.deviceTextTarget)
    } catch (error) {
      this.dispatchFlashError(`Error editing device: ${error.message}`, 'Device Selection')
    }
  }

  showElement(element) {
    if (!element) {
      throw new Error('Element not found')
    }

    if (this.hasHiddenClass) {
      element.classList.remove(this.hiddenClass)
    } else {
      element.classList.remove('hidden')
    }
  }

  hideElement(element) {
    if (!element) {
      throw new Error('Element not found')
    }

    if (this.hasHiddenClass) {
      element.classList.add(this.hiddenClass)
    } else {
      element.classList.add('hidden')
    }
  }

  addClass(jqueryElement, className) {
    if (jqueryElement && jqueryElement.length) {
      jqueryElement.addClass(className)
    }
  }

  removeClass(jqueryElement, className) {
    if (jqueryElement && jqueryElement.length) {
      jqueryElement.removeClass(className)
    }
  }
}
